import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, ArrowRight, Zap, FileText, Bell, BarChart3, 
  Calculator, Clock, Shield, Percent, Receipt, TrendingUp, Briefcase
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const SolutionAutoEntrepreneur = () => {
  const features = [
    {
      icon: Percent,
      title: "Mention TVA automatique",
      description: "La mention 'TVA non applicable, art. 293B du CGI' s'ajoute automatiquement à vos factures."
    },
    {
      icon: Calculator,
      title: "Simulateur URSSAF intégré",
      description: "Calculez vos cotisations en temps réel : 12,3% pour services, 21,1% pour libéral."
    },
    {
      icon: TrendingUp,
      title: "Suivi du plafond CA",
      description: "Visualisez où vous en êtes par rapport aux seuils (77 700€ services, 188 700€ ventes)."
    },
    {
      icon: Bell,
      title: "Relances automatiques",
      description: "Ne perdez plus de temps à relancer vos clients. SmartFacture le fait pour vous."
    },
    {
      icon: Receipt,
      title: "Livre des recettes auto",
      description: "Votre livre des recettes se remplit automatiquement, conforme aux obligations."
    },
    {
      icon: Shield,
      title: "Conforme 2026",
      description: "Factures Factur-X prêtes pour la réforme de septembre 2026."
    }
  ];

  const steps = [
    { num: "1", title: "Créez votre compte", desc: "En 2 minutes, sans carte bancaire" },
    { num: "2", title: "Renseignez votre SIRET", desc: "Vos infos se remplissent automatiquement" },
    { num: "3", title: "Facturez vos clients", desc: "Factures conformes en 30 secondes" }
  ];

  const faqs = [
    {
      q: "SmartFacture AI est-il adapté aux auto-entrepreneurs ?",
      a: "Oui ! SmartFacture AI gère automatiquement la mention franchise TVA, le calcul des cotisations URSSAF et le suivi de votre plafond de CA."
    },
    {
      q: "Comment fonctionne le calcul des cotisations ?",
      a: "SmartFacture applique automatiquement le bon taux selon votre activité : 12,3% pour prestations de services (BIC), 21,1% pour professions libérales (BNC), 12,8% pour vente de marchandises."
    },
    {
      q: "Que se passe-t-il si je dépasse le plafond de CA ?",
      a: "SmartFacture vous alerte à l'approche des seuils. En cas de dépassement, l'outil vous accompagne dans la transition vers un autre statut."
    },
    {
      q: "Puis-je gérer plusieurs activités ?",
      a: "Oui, vous pouvez gérer des activités mixtes (services + ventes) avec des taux de cotisations différents."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Logiciel Facturation Auto-Entrepreneur | SmartFacture AI - URSSAF intégré</title>
        <meta name="description" content="Logiciel de facturation pour auto-entrepreneurs et micro-entrepreneurs. Mention TVA automatique, simulateur URSSAF, suivi plafond CA. Essai gratuit 7 jours." />
        <link rel="canonical" href="https://smartfacture-ai.com/solutions/auto-entrepreneur" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/outils/facture-gratuite" className="text-slate-600 hover:text-slate-900 hidden sm:block">
                Outil gratuit
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700">Essai gratuit</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-amber-500 to-orange-600 text-white py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-white/20 text-white border-white/30 mb-6">
                Solution Auto-Entrepreneur
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Facturation simplifiée pour auto-entrepreneurs
              </h1>
              <p className="text-xl text-amber-100 mb-8">
                Mention TVA automatique, calcul URSSAF en temps réel, suivi du plafond CA. Tout ce qu'il faut pour facturer en micro-entreprise.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-amber-600 hover:bg-amber-50">
                    Essai gratuit 7 jours
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/outils/facture-gratuite">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                    Créer une facture gratuite
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key Info Banner */}
        <section className="bg-amber-50 border-b border-amber-200 py-6">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div>
                <p className="text-2xl font-bold text-amber-600">77 700€</p>
                <p className="text-sm text-slate-600">Plafond services</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">188 700€</p>
                <p className="text-sm text-slate-600">Plafond ventes</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">12,3% - 21,1%</p>
                <p className="text-sm text-slate-600">Cotisations URSSAF</p>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Commencez en 3 étapes
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 font-bold text-xl mx-auto mb-4">
                    {s.num}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-slate-600">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Conçu pour les auto-entrepreneurs
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Toutes les fonctionnalités spécifiques au régime micro-entrepreneur, sans les complications.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-amber-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-slate-600">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* TVA Mention Example */}
        <section className="py-16 bg-slate-100">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Exemple de facture auto-entrepreneur
            </h2>
            <div className="bg-white rounded-xl p-6 shadow-lg max-w-2xl mx-auto">
              <div className="border-b pb-4 mb-4">
                <p className="font-semibold">Votre Entreprise</p>
                <p className="text-sm text-slate-500">SIRET: 123 456 789 00012</p>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span>Prestation de conseil</span>
                  <span className="font-semibold">500,00 €</span>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>500,00 €</span>
                </div>
                <p className="text-sm text-amber-600 mt-2 italic">
                  TVA non applicable, article 293 B du Code général des impôts
                </p>
              </div>
            </div>
            <p className="text-center text-slate-500 text-sm mt-4">
              Cette mention s'ajoute automatiquement à toutes vos factures
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-8 lg:p-12 text-white text-center">
              <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-80" />
              <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                Lancez-vous en auto-entrepreneur
              </h2>
              <p className="text-amber-100 mb-6">
                À partir de 14€/mois. Essai gratuit 7 jours sans engagement.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-white text-amber-600 hover:bg-amber-50">
                  Créer mon compte gratuitement
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-slate-100">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SolutionAutoEntrepreneur;
